/**
 * common — part of the German catalogue (the reference language).
 * Merged in `i18n/de.ts`; every key here must exist in `i18n/en/common.ts`.
 */

export const common = {
  /* ---------------- app chrome ---------------- */
  'app.title': 'Aether · VFR-Flugplanung',
  'app.description': 'VFR-Flugplanung für Europa: Karte, NavLog, Wetter, Gelände — und das LBA-Formblatt.',
  'app.notfound': 'Diese Seite gibt es nicht.',
  'app.toflights': 'Zu den Flügen',
  'footer.sources': 'Datenquellen & Lizenzen',
  loading: 'Lädt …',
  /** date/time locale for toLocaleString() */
  locale: 'de-DE',
  yes: 'ja',
  no: 'nein',

  /* ---------------- warnings (by code) ---------------- */
  'warn.route-incomplete':
    'Mindestens ein Abschnitt hat eine Entfernung, aber keine Zeit – Gesamtzeit und ETA sind unvollständig.',
  'warn.eta-next-day': 'ETA liegt am Folgetag – weit nach Sonnenuntergang!',
  'warn.eta-after-sunset': 'ETA liegt nach Sonnenuntergang!',
  'warn.eta-near-sunset': 'ETA liegt weniger als 30 min vor Sonnenuntergang.',
  'warn.exceeds-safe-time': 'Gesamtflugzeit überschreitet die sichere Flugzeit!',
  'warn.leg-unflyable': 'Abschnitt {leg}: Wind zu stark für die eingegebene TAS – keine Berechnung.',
  'warn.wind-ge-tas': 'Windkomponente ≥ TAS – Abschnitt rechnerisch nicht fliegbar',

  /* ---------------- field validation ---------------- */
  'invalid.time': 'Ungültige Zeit – HH:MM (UTC) erwartet, z. B. 11:30.',
  'invalid.hhmm': 'Ungültige Zeit – vier Ziffern erwartet, z. B. 1120.',
  'invalid.date': 'Ungültiges Datum – TT.MM.JJJJ erwartet, z. B. 17.8.2026.',
  'invalid.number': 'Keine gültige Zahl.',
  'invalid.heading': 'Ungültiger Kurs – 0 bis 360 Grad erwartet.',
  'invalid.wind': 'Ungültiger Wind – GGG/KK erwartet, z. B. 270/10 oder 270/10G20.',
  'invalid.wca': 'Ungültiger Luvwinkel – z. B. 5+ oder 3-.',

  /* ---------------- API / IO errors (by code) ---------------- */
  'err.network': 'Keine Verbindung zum Server.',
  'err.unauthenticated': 'Bitte anmelden.',
  'err.forbidden': 'Nicht erlaubt.',
  'err.not_found': 'Nicht gefunden.',
  'err.bad_credentials': 'E-Mail oder Passwort stimmt nicht.',
  'err.conflict': 'Der Eintrag existiert bereits.',
  'err.version_conflict': 'Der Plan wurde inzwischen anderswo geändert.',
  'err.invalid': 'Eingabe nicht gültig.',
  'err.invalid_token': 'Der Link ist ungültig oder abgelaufen.',
  'err.internal': 'Server-Fehler.',
  'err.unavailable': 'Dienst gerade nicht verfügbar.',
  'err.http': 'Anfrage fehlgeschlagen (HTTP {status}).',
  'err.pdf': 'PDF konnte nicht erzeugt werden: {error}',
  'err.file': 'Datei konnte nicht gelesen werden: {error}',
  'err.share': 'Freigabe fehlgeschlagen: {error}',
  'err.notAnObject': 'kein Objekt',

  /* ---------------- wie frisch die Daten sind (autoload.ts) ---------------- */
  // Eine Zeile je Quelle, im Kopf des Abschnitts: wann zuletzt gelesen, ein ⟳,
  // ein ⓘ. Alles andere über die Herkunft steht hinter dem ⓘ.
  'fresh.fam.wx': 'Wetter',
  'fresh.fam.winds': 'Wind',
  'fresh.fam.terrain': 'Gelände',
  'fresh.fam.geo': 'Platzdaten',
  'fresh.at': 'aktualisiert {time}',
  'fresh.ago': 'vor {n} min',
  'fresh.now': 'gerade aktualisiert',
  'fresh.busy': 'wird aktualisiert',
  'fresh.refresh': 'Aktualisieren',
  'fresh.info': 'Woher kommen diese Daten?',
  'fresh.obs': 'METAR {time}',
  'fresh.err.wx': 'Wetter gerade nicht erreichbar – wird erneut versucht.',
  'fresh.err.winds': 'Wind gerade nicht erreichbar – wird erneut versucht.',
  'fresh.err.terrain': 'Gelände gerade nicht erreichbar – wird erneut versucht.',
  'fresh.err.geo': 'Platzdaten gerade nicht erreichbar – wird erneut versucht.',
  'src.info.wx':
    'METAR und TAF der Plätze im Plan. Sie kommen vom US-Wetterdienst NOAA und werden alle zehn Minuten neu geholt. Ersetzt keine amtliche Flugwetterberatung.',
  'src.info.winds':
    'Windvorhersage für die Mitte jedes Abschnitts, auf geplanter Höhe und Uhrzeit. Sie kommt von Open-Meteo auf Grundlage der Modelle des Deutschen Wetterdienstes. Planungshilfe, keine Flugwetterberatung.',
  'src.info.terrain':
    'Höchstes Gelände und höchstes Hindernis im Streifen entlang der Strecke. Geländehöhen von Mapterhorn, Hindernisse von openAIP. Manche Hindernisse sind ohne Höhe veröffentlicht. Hinweis, keine Freigabe.',
  'src.info.geo':
    'Plätze, Bahnen, Frequenzen, Funkfeuer, Meldepunkte und Lufträume aus der offenen Luftfahrt-Datenbank openAIP. Sie kommen, sobald die Strecke steht. Nicht amtlich – vor dem Flug gegen AIP und NOTAM prüfen.',
  'src.info.airspace':
    'Lufträume entlang der Strecke, geprüft in der geplanten Höhe. Aus der offenen Luftfahrt-Datenbank openAIP. Hinweis, ersetzt keine Freigabe.',
  'src.more': 'Alle Datenquellen & Lizenzen',
  'src.page.title': 'Datenquellen & Lizenzen',
  'src.page.lead':
    'Aether zeigt Daten öffentlicher Dienste. Hier steht, welche das sind und unter welcher Lizenz. Keine davon ersetzt die amtliche Flugvorbereitung: AIP, NOTAM und Flugwetterberatung bleiben verbindlich.',
  'src.page.wx.title': 'Wetter – METAR und TAF',
  'src.page.wx.body':
    'Flugplatz-Wettermeldungen und -vorhersagen des NOAA Aviation Weather Center (US National Weather Service). Als Werk der US-Bundesregierung gemeinfrei, eine Namensnennung ist nicht vorgeschrieben. Amtliche Beratung in Deutschland: Deutscher Wetterdienst, Luftfahrt.',
  'src.page.winds.title': 'Wind und Temperatur in der Höhe',
  'src.page.winds.body':
    'Wetterdaten von Open-Meteo.com, lizenziert unter CC BY 4.0. Grundlage sind die ICON-Modelle des Deutschen Wetterdienstes (DWD), ebenfalls CC BY 4.0. Vorhersagedaten – eine Planungshilfe, keine Flugwetterberatung.',
  'src.page.geo.title': 'Platzdaten, Lufträume, Hindernisse',
  'src.page.geo.body':
    'Flugplätze, Bahnen, Frequenzen, Funkfeuer, Meldepunkte, Lufträume und Hindernisse von openAIP – © openAIP contributors, lizenziert unter CC BY-NC 4.0. Von Freiwilligen gepflegt und nicht amtlich. Vor dem Flug gegen AIP und NOTAM prüfen.',
  'src.page.terrain.title': 'Gelände',
  'src.page.terrain.body':
    'Höhenmodell von Mapterhorn, zusammengesetzt aus mehreren offenen Datensätzen, die dort einzeln aufgeführt sind. Auflösung rund 50 m. Hindernisse im Geländeprofil von openAIP.',
  'src.page.map.title': 'Karte',
  'src.page.map.body':
    'Kartenbild von Protomaps, Kartendaten © OpenStreetMap-Mitwirkende unter ODbL. Weitere Kartenstile von OpenFreeMap – © OpenMapTiles, © OpenStreetMap-Mitwirkende.',
  'src.page.var.title': 'Mißweisung',
  'src.page.var.body': 'World Magnetic Model (WMM) von NOAA/NCEI und dem British Geological Survey. Gemeinfrei.',
  'src.page.ac.title': 'Flugzeug-Vorlagen',
  'src.page.ac.body':
    'Startwerte aus veröffentlichten Handbüchern und Musterzulassungen (POH/AFM/TCDS), je Vorlage einzeln nachgewiesen. Kein Ersatz für Betriebshandbuch und Wägebericht Ihres Flugzeugs.',
  'src.page.form.title': 'Das Formblatt',
  'src.page.form.body': '„Flugdurchführungsplan VFR“, LBA-Formblatt fsm 1/87.',
  'src.page.back': 'Zurück',

  /* ---------------- navigation / session ---------------- */
  'nav.aria': 'Bereiche',
  'nav.flights': 'Flüge',
  'nav.settings': 'Einstellungen',
  'nav.sources': 'Datenquellen',
  'nav.more': 'Mehr',
  'nav.signout': 'Abmelden',
  'nav.signin': 'Anmelden',
  'nav.noaccount': 'Ohne Konto planen',
  'toast.close': 'Schließen',
  'session.expired':
    'Sitzung abgelaufen. Es wird nichts mehr auf dem Server gespeichert; die Eingaben bleiben in diesem Browser.',
  'session.expired.signin': 'Neu anmelden',

  /* ---------------- Flugzeuge (/aircraft) ---------------- */
  'nav.aircraft': 'Flugzeuge',

  /* ---------------- typed banners (app/severity.tsx) ---------------- */
  'bn.more': '+{n} weitere Hinweise',
  'bn.fuel-short': 'Mindestmenge {need} l über dem Vorrat {have} l.',
  'bn.cap-legs': 'Nur zwölf Abschnitte passen aufs Formblatt – {n} weitere werden geflogen, aber nicht eingetragen.',
  'bn.offline': 'Offline – Änderungen werden gesichert, sobald die Verbindung steht.',
} as const;
